
/* eslint-disable max-lines */
// TODO: Clean code
import { createContext, useState } from 'react'

import { api } from 'utils/api'
import { getError } from 'utils/helpers/getError'
import { useCustomContext } from 'utils/helpers/useCustomContext'
import { GenericResponse, GenericStateType } from 'utils/types'

import {
  AssistanceRequestType, AssistanceResponseType, VehicleContextType, VehicleFormVinUsedType, VehicleIdentifierInputType,
  VehicleLocationType, VehiclePlateNumberInputType, VehiclePlateNumberMessageType, VehicleRegisterWithShortVinType,
  VehicleRegisterWithVinType, VehiclesStateType, VehicleType,
} from './vehiclesTypes'

const INITIAL_VEHICLE_CONTEXT: VehicleContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  setLocationLoading: () => null,
  setLocationError: () => null,
  locationClearError: () => null,
  setAddVehicleLoading: () => null,
  setAddVehicleError: () => null,
  addVehicleClearError: () => null,
  addNewVehicle: () => new Promise(() => null),
  addNewVehicleWithShortVin: () => new Promise(() => null),
  addNewVehicleWithVin: () => new Promise(() => null),
  checkCurrentVehicle: () => new Promise(() => null),
  deleteVehicle: () => new Promise(() => false),
  getVehicleByDeviceImei: () => new Promise(() => null),
  getVehicleById: () => null,
  getVehicleByQrDeviceId: () => new Promise(() => null),
  getVehicles: () => new Promise(() => null),
  resetNewVehicleAdded: () => null,
  resetState: () => null,
  sendFormVinUsed: () => new Promise(() => false),
  setVehicle: () => new Promise(() => null),
  setVehicles: () => new Promise(() => null),
  getVehicleLocation: () => null,
  getCheckPlateNumber: () => new Promise(() => null),
  getCheckIdentifier: () => new Promise(() => null),
  sendRoadAssistanceRequest: () => new Promise(() => null),
  vehicleState: {
    error: '',
    loading: false,
    state: {
      errorLocation: '',
      locationLoading: false,
      errorAddVehicle: '',
      addVehicleLoading: false,
      vehicles: [],
      vehicle: null,
      newVehicle: null,
      formVinUsed: null,
      newVehicleAdded: false,
      extraLoading: false,
      vehicleLocation: {
        latitude: 0,
        longitude: 0,
        orientation: 0,
        speed: 0,
        timer: 0,
        isOnline:null,
        mileage: undefined
      },
    },
  },
}

const VehicleContext = createContext<VehicleContextType>(
  INITIAL_VEHICLE_CONTEXT,
)

const VehiclesProvider: React.FC = ({ children }) => {
  const [vehicleState, setVehicleState] = useState<
    GenericStateType<VehiclesStateType>
  >(INITIAL_VEHICLE_CONTEXT.vehicleState)

  const setLoading = (value = true) => setVehicleState(prevState => ({ ...prevState, loading: value }))

  const setLocationLoading = (value = true) =>
    setVehicleState(prevState => ({ ...prevState, state: { ...prevState.state, extraLoading: value } }))

  const setAddVehicleLoading = (value = true) =>
    setVehicleState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        addVehicleLoading: value,
      },
    }))

  const setError = (msg: string) =>
    setVehicleState(prevState => ({ ...prevState, loading: false, error: msg }))

  const setLocationError = (msg: string) =>
    setVehicleState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        errorLocation: msg,
        locationLoading: false,
      },
    }))

  const setAddVehicleError = (msg: string) =>
    setVehicleState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        errorAddVehicle: msg,
        addVehicleLoading: false,
      },
    }))

  const clearError = () =>
    setVehicleState(prevState => ({ ...prevState, error: '' }))

  const locationClearError = () =>
    setVehicleState(prevState => ({ ...prevState, state: { ...prevState.state, errorLocation: '' } }))

  const addVehicleClearError = () =>
    setVehicleState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        errorAddVehicle: '',
        addVehicleLoading: false,
      },
    }))

  const setVehicle = async (vehicle: VehicleType | null) => {
    if (vehicle)
      await localStorage.setItem('ACTIVE_VEHICLE', JSON.stringify(vehicle))
    else
      await localStorage.removeItem('ACTIVE_VEHICLE')

    setVehicleState(prevState => ({
      ...prevState,
      loading: false,
      error: '',
      state: {
        ...prevState.state,
        vehicle,
      },
    }))

    return vehicle
  }

  const setVehicles = async (vehicles: VehicleType[]) => {
    if (vehicles)
      await localStorage.setItem('ALL_VEHICLES', JSON.stringify(vehicles))
    else
      await localStorage.removeItem('ALL_VEHICLES')

    setVehicleState(prevState => ({
      ...prevState,
      loading: false,
      error: '',
      state: {
        ...prevState.state,
        vehicles,
      },
    }))
  }

  const refetchVehicles = async () => {
    try {
      setLoading()

      const { data: { payload: vehicles } } = await api.get<GenericResponse<VehicleType[]>>('vehicles')

      const activeVehicleString: string | null = await localStorage.getItem('ACTIVE_VEHICLE')

      if (activeVehicleString && vehicles.length > 0) {
        const activeVehicle: VehicleType = JSON.parse(activeVehicleString)
        const vehicle = vehicles.find(({ id }) => id === activeVehicle.id)

        // Todo: debemos enviar esta informacion a la app
        if (vehicle) {
          setVehicle(vehicle)
          setVehicles(vehicles)
        } else {
          setVehicle(null)
          setVehicles(vehicles)
        }
      } else if (vehicles.length > 0) {
        setVehicle(vehicles[0])
        setVehicles(vehicles)
      } else {
        setVehicle(null)
        setVehicles(vehicles)
      }
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const getVehicles = async () => {
    setLoading()
    refetchVehicles()
  }

  const getVehicleById = async (id: string) => {
    try {
      setLoading()

      const {
        data: { payload: vehicle },
      } = await api.get<GenericResponse<VehicleType>>(`Vehicles/${id}`)

      if (vehicle) {
        await localStorage.setItem('ACTIVE_VEHICLE', JSON.stringify(vehicle))
        setVehicleState(prevState => ({
          ...prevState,
          loading: false,
          state: {
            ...prevState.state,
            vehicle,
          },
        }))
      }
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const checkCurrentVehicle = async () => {
    try {
      const activeVehicleString = await localStorage.getItem('ACTIVE_VEHICLE')

      if (!activeVehicleString) return

      const { id }: VehicleType = JSON.parse(activeVehicleString)

      getVehicleById(id)
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const getVehicleByDeviceImei = async (deviceImei: string) => {
    try {
      setAddVehicleLoading()

      const value = deviceImei.toUpperCase()
      const { data: { payload } } = await api.get<GenericResponse<VehicleType>>(`Vehicles/FindByDeviceImei/${value}`)

      setVehicleState(prevState => ({
        ...prevState,
        loading: false,
        state: {
          ...prevState.state,
          addVehicleLoading: false,
          newVehicle: payload,
        },
      }))

      return true
    } catch (err) {
      const message = getError(err)

      setAddVehicleError(message)

      return false
    }
  }

  const getVehicleByQrDeviceId = async (qrDeviceId: string) => {
    try {
      setAddVehicleLoading()

      const value = qrDeviceId.toLowerCase()
      const { data: { payload } } = await api.get<GenericResponse<VehicleType>>(`Vehicles/FindByQrDeviceId/${value}`)

      setVehicleState(prevState => ({
        ...prevState,
        loading: false,
        state: {
          ...prevState.state,
          addVehicleLoading: false,
          newVehicle: payload,
        },
      }))

      return true
    } catch (err) {
      const message = getError(err)

      setAddVehicleError(message)

      return false
    }
  }

  const addNewVehicle = async (qrDeviceId?: string, deviceImei?: string) => {
    try {
      setAddVehicleLoading()

      const data = { QrId: qrDeviceId?.toLowerCase(), DeviceImei: deviceImei?.toUpperCase() }
      const { data: { payload: vehicle } } = await api.post<GenericResponse<VehicleType>>('Vehicles/AddVehicle', data)

      getVehicles()

      return vehicle
    } catch (err) {
      const message = getError(err)

      setAddVehicleError(message)

      return null
    }
  }

  const sendFormVinUsed = async (data: VehicleFormVinUsedType) => {
    try {
      setLoading()
      await api.post('ConsumerPropspects/CreateConsumerPropspects', data)
      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const addNewVehicleWithVin = async (data: VehicleRegisterWithVinType) => {
    try {
      setAddVehicleLoading()

      const url = 'Vehicles/CreateVehicleByVin'
      const { data: { payload: vehicle } } = await api.post<GenericResponse<VehicleType>>(url, data)

      getVehicles()

      return vehicle
    } catch (err) {
      const message = getError(err)

      setAddVehicleError(message)

      return null
    }
  }

  const addNewVehicleWithShortVin = async (data: VehicleRegisterWithShortVinType) => {
    try {
      setAddVehicleLoading()

      const url = 'Vehicles/CreateVehicleByShortenedVin'
      const { data: { payload: vehicle } } = await api.post<GenericResponse<VehicleType>>(url, data)

      getVehicles()

      return vehicle
    } catch (err) {
      const message = getError(err)

      setAddVehicleError(message)

      return null
    }
  }

  const deleteVehicle = async (id: string) => {
    try {
      setLoading()

      const {
        data: { payload },
      } = await api.get<GenericResponse<string>>(`Vehicles/${id}/Delete`)

      setVehicleState(prevState => ({
        ...prevState,
        error: '',
        state: {
          ...prevState.state,
          vehicle: null,
          vehicles: prevState.state.vehicles.filter(
            (vehicle: VehicleType) => vehicle.id !== payload,
          ),
        },
      }))

      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const getVehicleLocation = async (id: string, timer: boolean, isRestricted?: boolean) => {
    if (isRestricted) return

    try {
      !timer && setLocationLoading()

      const { data: { payload } } = await api.get<GenericResponse<VehicleLocationType>>(`Vehicles/${id}/GeolocationV2`)

      setVehicleState(prevState => ({
        ...prevState,
        error: '',
        loading: false,
        state: {
          ...prevState.state,
          vehicleLocation: {
            ...vehicleState.state.vehicleLocation,
            latitude: payload.latitude,
            longitude: payload.longitude,
            orientation: payload.orientation,
            timer: parseInt(payload.timer.toString()),
            speed: payload.speed,
            isOnline: payload.isOnline,
            mileage: payload.mileage || undefined
          },
        },
      }))

      locationClearError()
      setLocationLoading(false)
    } catch (err) {
      const message = getError(err)

      setVehicleState(prevState => ({
        ...prevState,
        error: '',
        loading: false,
        state: {
          ...prevState.state,
          vehicleLocation: {
            ...prevState.state.vehicleLocation,
            timer: 10000,
          },
        },
      }))

      setLocationError(message)
    }
  }

  const getCheckPlateNumber = async ({ plateNumber }: VehiclePlateNumberInputType) => {
    try {

      setLoading()

      const { data: { payload, message } } =
        await api.get<GenericResponse<boolean>>(`Vehicles/CheckPlateNumber/${plateNumber}`)

      setLoading(false)

      return {
        result: true,
        msg: message as VehiclePlateNumberMessageType,
        isDevice: payload,
      }
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return {
        result: false,
        msg: msg as VehiclePlateNumberMessageType,
        isDevice: false,
      }
    }
  }

  const getCheckIdentifier = async ({ identifier }: VehicleIdentifierInputType) => {
    try {

      setLoading()

      const { data: { payload, message } } =
        await api.get<GenericResponse<{ isGurtamDevice: boolean, vin: string }>>
          (`Vehicles/CheckIdentifier/${identifier}`)

      setLoading(false)

      return {
        result: true,
        msg: message as VehiclePlateNumberMessageType,
        isDevice: payload.isGurtamDevice,
        vin: payload.vin,
      }
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return {
        result: false,
        msg: msg as VehiclePlateNumberMessageType,
        isDevice: false,
        vin: '',
      }
    }
  }

  const sendRoadAssistanceRequest = async (data: AssistanceRequestType) => {
    try {
      setLoading(true)

      const { data: { payload: { id } } } =
        await api.post<GenericResponse<AssistanceResponseType>>('Assistance/RequestAssistance', data)

      setLoading(false)

      return id
    } catch (err) {
      const message = getError(err)

      setError(message)

      return null
    }
  }

  const resetNewVehicleAdded = () =>
    setVehicleState(prevState => ({
      ...prevState,
      state: { ...prevState.state, newVehicleAdded: false, newVehicle: null },
    }))

  const resetState = () =>
    setVehicleState(prevState => ({
      ...prevState,
      error: '',
      loading: false,
      state: {
        ...prevState.state,
        vehicles: [],
        vehicle: null,
        newVehicleAdded: false,
        newVehicle: null,
      },
    }))

  return (
    <VehicleContext.Provider
      value={{
        vehicleState,
        setLoading,
        setError,
        clearError,
        locationClearError,
        setLocationError,
        setLocationLoading,
        setAddVehicleLoading,
        addVehicleClearError,
        setAddVehicleError,
        getVehicles,
        getVehicleById,
        checkCurrentVehicle,
        getVehicleByQrDeviceId,
        addNewVehicle,
        setVehicle,
        setVehicles,
        resetNewVehicleAdded,
        getVehicleByDeviceImei,
        resetState,
        sendFormVinUsed,
        addNewVehicleWithShortVin,
        addNewVehicleWithVin,
        deleteVehicle,
        getVehicleLocation,
        getCheckPlateNumber,
        getCheckIdentifier,
        sendRoadAssistanceRequest,
      }}>
      {children}
    </VehicleContext.Provider>
  )
}

const useVehicles = () => useCustomContext<VehicleContextType>(VehicleContext)

export { useVehicles, VehiclesProvider }
